import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  BadgeCheck,
  Building2,
  CheckCircle2,
  Loader2,
  Sparkles,
  TimerReset,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { StatusChip } from "@/components/StatusChip";
import { formatDateShort, formatPhp } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

function statusMeta(txnStatus) {
  if (txnStatus === "paid") return { chip: "Active", label: "Paid" };
  if (txnStatus === "failed") return { chip: "Disputed", label: "Failed" };
  if (txnStatus === "expired") return { chip: "Disputed", label: "Expired" };
  if (txnStatus === "refunded") return { chip: "Archived", label: "Refunded" };
  return { chip: "Archived", label: txnStatus };
}

function TransactionRow({ txn }) {
  const s = statusMeta(txn.status);
  return (
    <tr className="border-t border-gray-100" data-testid={`transaction-row-${txn.id}`}>
      <td className="px-4 py-3 text-sm text-gray-700">
        {formatDateShort(txn.created_at)}
      </td>
      <td className="px-4 py-3 text-sm font-semibold text-gray-900">
        {formatPhp((txn.amount || 0) / 100)}
      </td>
      <td className="px-4 py-3 text-xs text-gray-500">
        {txn.tier_name || "—"}
      </td>
      <td className="px-4 py-3 text-xs text-gray-500">{txn.method || "—"}</td>
      <td className="px-4 py-3">
        <StatusChip status={s.chip} />
        <span className="ml-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
          {s.label}
        </span>
      </td>
    </tr>
  );
}

export default function BillingPage() {
  const { user, refreshMe } = useAuth();
  const [plan, setPlan] = useState(null);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [selectedTier, setSelectedTier] = useState("tier_monthly");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: p }, { data: t }] = await Promise.all([
        api.get("/billing/plan"),
        api.get("/billing/transactions"),
      ]);
      setPlan(p);
      setTxns(t);
    } catch (e) {
      toast.error(formatApiError(e, "Failed to load billing"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    refreshMe();
  }, [load, refreshMe]);

  const startCheckout = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        platform: "web",
        tier_id: selectedTier,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e, "Failed to start checkout"));
      setStarting(false);
    }
  };

  const isPro = plan?.plan === "pro";
  const trial = plan?.trial;
  const tiers = useMemo(() => plan?.tiers || [], [plan]);

  return (
    <section
      className="mx-auto w-full max-w-5xl px-6 py-12 md:px-10 md:py-16 mb-rise"
      data-testid="page-billing"
    >
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-[#1B2B4B]">
          Billing
        </div>
        <h1 className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
          Subscription
        </h1>
        <p className="mt-3 max-w-xl text-base leading-relaxed text-gray-500">
          MatchBroker is free for the first 30 days — or your first 3 successful
          matches, whichever comes first. Pick a paid tier below to keep going.
        </p>
      </div>

      <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2">
        <div
          className="rounded-2xl border border-gray-200 bg-white p-8 shadow-mb-card"
          data-testid="current-plan-card"
        >
          <div className="flex items-center gap-3">
            <span
              className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${
                isPro ? "bg-[#35622F]/10 text-[#35622F]" : "bg-gray-100 text-gray-500"
              }`}
            >
              {isPro ? <BadgeCheck className="h-5 w-5" /> : <Building2 className="h-5 w-5" />}
            </span>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                Current plan
              </div>
              <div
                className="text-2xl font-bold text-gray-900"
                data-testid="current-plan-label"
              >
                {isPro
                  ? plan?.subscription_tier
                    ? `MatchBroker · ${plan.subscription_tier.replace("tier_", "").replace("_", " ")}`
                    : "MatchBroker Pro"
                  : trial?.expired
                    ? "Trial expired"
                    : "Free trial"}
              </div>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-4">
            <div className="rounded-xl bg-gray-50 px-4 py-3">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                Active listings
              </div>
              <div
                className="mt-1 text-xl font-bold text-gray-900"
                data-testid="plan-active-listings"
              >
                {plan?.active_listings ?? "—"}
              </div>
            </div>
            {isPro ? (
              <div className="rounded-xl bg-gray-50 px-4 py-3">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                  Renews / Expires
                </div>
                <div
                  className="mt-1 text-xl font-bold text-gray-900"
                  data-testid="plan-expires-at"
                >
                  {plan?.subscription_expires_at
                    ? formatDateShort(plan.subscription_expires_at)
                    : "—"}
                </div>
              </div>
            ) : (
              <div
                className="rounded-xl bg-gray-50 px-4 py-3"
                data-testid="plan-trial-remaining"
              >
                <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                  Trial remaining
                </div>
                <div className="mt-1 text-sm font-bold text-gray-900">
                  {trial?.expired ? (
                    <span className="text-brand-navy">Ended</span>
                  ) : (
                    <>
                      {trial?.trial_days_left ?? 0} days ·{" "}
                      {trial?.trial_matches_left ?? 0} matches
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {!isPro && trial && !trial.expired && (
            <div className="mt-6 flex items-start gap-2 rounded-xl border border-brand-green/20 bg-brand-green/5 px-3 py-2 text-xs text-brand-green">
              <TimerReset className="mt-0.5 h-3.5 w-3.5 flex-none" />
              Hybrid trial: 30 days OR 3 successful matches — whichever comes first.
            </div>
          )}
          {!isPro && (
            <p className="mt-4 text-xs text-gray-500">
              Signed in as{" "}
              <span className="font-semibold text-gray-800">{user?.email}</span>.
            </p>
          )}
        </div>

        <div
          className={`relative overflow-hidden rounded-2xl border p-8 shadow-mb-card ${
            isPro ? "border-gray-200 bg-white" : "border-brand-green/30 bg-white"
          }`}
          data-testid="upgrade-card"
        >
          <div className="flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-brand-green/10 text-brand-green">
              <Sparkles className="h-5 w-5" />
            </span>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-brand-green">
                {isPro ? "Extend or switch tier" : "Pick your plan"}
              </div>
              <div className="text-2xl font-bold text-gray-900">
                Choose a MatchBroker plan
              </div>
            </div>
          </div>

          <ul
            className="mt-6 space-y-3"
            data-testid="tier-list"
          >
            {tiers.map((t) => {
              const isSelected = selectedTier === t.id;
              return (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedTier(t.id)}
                    className={`mb-focus-ring flex w-full items-start gap-3 rounded-xl border px-4 py-3 text-left transition ${
                      isSelected
                        ? "border-brand-green bg-brand-green/5"
                        : "border-gray-200 bg-white hover:border-gray-300"
                    }`}
                    data-testid={`tier-option-${t.id}`}
                  >
                    <span
                      className={`mt-1 flex h-4 w-4 flex-none items-center justify-center rounded-full border ${
                        isSelected
                          ? "border-brand-green bg-brand-green"
                          : "border-gray-300 bg-white"
                      }`}
                    >
                      {isSelected && (
                        <CheckCircle2 className="h-3 w-3 text-white" />
                      )}
                    </span>
                    <div className="flex-1">
                      <div className="flex items-baseline justify-between">
                        <div className="text-sm font-semibold text-gray-900">
                          {t.name}
                        </div>
                        <div className="text-sm font-bold text-gray-900">
                          {t.label}
                        </div>
                      </div>
                      <div className="mt-0.5 text-xs text-gray-500">
                        {t.subtitle}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>

          <Button
            type="button"
            onClick={startCheckout}
            disabled={starting || loading}
            className="mb-focus-ring mt-6 h-11 w-full rounded-xl bg-[#1B2B4B] text-sm font-semibold text-white hover:bg-[#14213A] disabled:opacity-60"
            data-testid="start-checkout-button"
          >
            {starting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Starting checkout…
              </>
            ) : (
              "Continue to checkout"
            )}
          </Button>
          <div className="mt-2 text-center text-[11px] text-gray-400">
            Payments via PayMongo · GCash · Maya · QR Ph · Card · Test mode
          </div>
        </div>
      </div>

      <div
        className="mt-12 rounded-2xl border border-gray-200 bg-white shadow-mb-card"
        data-testid="transactions-card"
      >
        <div className="border-b border-gray-100 px-6 py-4">
          <div className="text-sm font-semibold text-gray-900">Transaction history</div>
          <div className="text-xs text-gray-500">
            {txns.length === 0
              ? "No transactions yet."
              : `${txns.length} transaction${txns.length === 1 ? "" : "s"}`}
          </div>
        </div>
        {txns.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-gray-50 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Tier</th>
                  <th className="px-4 py-3">Method</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {txns.map((t) => (
                  <TransactionRow key={t.id} txn={t} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="mt-8 text-center text-xs text-gray-400">
        Billed by {plan?.business_entity || "Vancor Distribution, OPC"} · PayMongo test-mode ·
        <Link
          to="/dashboard/inventory"
          className="ml-1 text-[#1B2B4B] hover:underline"
        >
          Back to Inventory
        </Link>
      </p>
    </section>
  );
}
